/**
 * Persistent Upload Queue Manager
 * 
 * Uses IndexedDB to persist upload queue across tab switches, app backgrounding,
 * and connectivity drops. Automatically resumes when the page regains focus or
 * connectivity returns.
 * 
 * Architecture:
 * - Files are stored in IndexedDB as ArrayBuffers so they survive tab suspension
 * - Queue processes 1 file at a time sequentially (await each upload before starting next)
 * - Auto-retries with exponential backoff on failure
 * - Resumes on visibility change (tab switch back) and online events
 * - Provides reactive state via callbacks for UI updates
 */

const DB_NAME = "dh-upload-queue";
const DB_VERSION = 1;
const STORE_NAME = "pending-uploads";
const MAX_RETRIES = 5;
const BASE_RETRY_DELAY = 3000;

// Maximum canvas dimension to avoid mobile browser memory crashes
const MAX_CANVAS_DIM = 2048;
// Maximum file size for compression attempt
const MAX_COMPRESS_SIZE = 12 * 1024 * 1024; // 12MB

export type QueuedFile = {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  arrayBuffer: ArrayBuffer;
  folder: string;
  leadId?: number;
  projectId?: number;
  status: "queued" | "compressing" | "uploading" | "saving" | "done" | "error";
  progress: number;
  error?: string;
  retryCount: number;
  addedAt: number;
};

export type QueueStatus = {
  items: Array<Omit<QueuedFile, "arrayBuffer">>;
  activeCount: number;
  totalCount: number;
  doneCount: number;
  errorCount: number;
  isProcessing: boolean;
};

type QueueListener = (status: QueueStatus) => void;

type BatchCompleteCallback = (completedItems: Array<{ folder: string; projectId?: number; leadId?: number; fileName: string }>) => void;

class UploadQueueManager {
  private db: IDBDatabase | null = null;
  private listeners: Set<QueueListener> = new Set();
  private batchCallbacks: Set<BatchCompleteCallback> = new Set();
  private isRunning = false; // true while the sequential loop is active
  private inMemoryStatus: Map<string, Omit<QueuedFile, "arrayBuffer">> = new Map();
  private initialized = false;
  private kickTimer: ReturnType<typeof setTimeout> | null = null;
  private completedInBatch: Array<{ folder: string; projectId?: number; leadId?: number; fileName: string }> = [];

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    try {
      this.db = await this.openDB();
    } catch (err) {
      console.warn("[UploadQueue] IndexedDB not available, using memory-only mode");
    }

    // Resume uploads when tab becomes visible again
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        console.log("[UploadQueue] Tab visible, resuming queue...");
        this.kick(500);
      }
    });

    // Resume uploads when connectivity returns
    window.addEventListener("online", () => {
      console.log("[UploadQueue] Back online, resuming queue...");
      this.kick(1000);
    });

    // Load any pending items from previous session
    await this.loadPendingItems();
    this.kick(200);
  }

  // ─── IndexedDB helpers ───────────────────────────────────────────

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async loadPendingItems(): Promise<void> {
    if (!this.db) return;
    try {
      const tx = this.db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      const items = await new Promise<QueuedFile[]>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      for (const item of items) {
        if (item.status === "done") {
          this.removeFromDB(item.id);
        } else {
          // Reset in-progress items back to queued so they get picked up
          const status = (item.status === "uploading" || item.status === "compressing" || item.status === "saving")
            ? "queued" : item.status;
          this.inMemoryStatus.set(item.id, {
            id: item.id,
            fileName: item.fileName,
            fileSize: item.fileSize,
            fileType: item.fileType,
            folder: item.folder,
            leadId: item.leadId,
            projectId: item.projectId,
            status,
            progress: status === "queued" ? 0 : item.progress,
            error: item.error,
            retryCount: item.retryCount,
            addedAt: item.addedAt,
          });
        }
      }
      this.notifyListeners();
    } catch (err) {
      console.warn("[UploadQueue] Failed to load pending items:", err);
    }
  }

  private async saveToDb(item: QueuedFile): Promise<void> {
    if (!this.db) return;
    try {
      const tx = this.db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(item);
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (err) {
      console.warn("[UploadQueue] Failed to save to IndexedDB:", err);
    }
  }

  private async removeFromDB(id: string): Promise<void> {
    if (!this.db) return;
    try {
      const tx = this.db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(id);
    } catch (err) {
      console.warn("[UploadQueue] Failed to remove from IndexedDB:", err);
    }
  }

  private async getFromDB(id: string): Promise<QueuedFile | null> {
    if (!this.db) return null;
    try {
      const tx = this.db.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(id);
      return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch {
      return null;
    }
  }

  // ─── Subscription ────────────────────────────────────────────────

  subscribe(listener: QueueListener): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  /** Subscribe to batch completion events (fires when all queued items finish) */
  onBatchComplete(callback: BatchCompleteCallback): () => void {
    this.batchCallbacks.add(callback);
    return () => this.batchCallbacks.delete(callback);
  }

  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach(l => {
      try { l(status); } catch { /* ignore listener errors */ }
    });
  }

  getStatus(): QueueStatus {
    const items = Array.from(this.inMemoryStatus.values());
    const activeCount = items.filter(i =>
      i.status === "compressing" || i.status === "uploading" || i.status === "saving"
    ).length;
    return {
      items,
      activeCount,
      totalCount: items.length,
      doneCount: items.filter(i => i.status === "done").length,
      errorCount: items.filter(i => i.status === "error").length,
      isProcessing: this.isRunning,
    };
  }

  // ─── Public API ──────────────────────────────────────────────────

  /**
   * Add files to the upload queue.
   * Reads files into ArrayBuffers one at a time to avoid memory spikes.
   */
  async addFiles(
    files: File[],
    options: { folder: string; leadId?: number; projectId?: number }
  ): Promise<void> {
    await this.init();

    for (const file of files) {
      const id = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      // Track in memory immediately so UI shows the item
      this.inMemoryStatus.set(id, {
        id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || "application/octet-stream",
        folder: options.folder,
        leadId: options.leadId,
        projectId: options.projectId,
        status: "queued",
        progress: 0,
        retryCount: 0,
        addedAt: Date.now(),
      });
      this.notifyListeners();

      // Read file into ArrayBuffer
      try {
        const arrayBuffer = await file.arrayBuffer();
        const item: QueuedFile = {
          id,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type || "application/octet-stream",
          arrayBuffer,
          folder: options.folder,
          leadId: options.leadId,
          projectId: options.projectId,
          status: "queued",
          progress: 0,
          retryCount: 0,
          addedAt: Date.now(),
        };
        await this.saveToDb(item);
        console.log(`[UploadQueue] Queued: ${file.name} (${(file.size / 1024).toFixed(0)}KB)`);
      } catch (err: any) {
        console.error(`[UploadQueue] Failed to read file ${file.name}:`, err);
        this.updateItemStatus(id, {
          status: "error",
          error: `Failed to read file: ${err.message || "Unknown error"}`,
        });
      }

      // Yield to main thread between files
      await new Promise(r => setTimeout(r, 50));
    }

    this.notifyListeners();
    this.kick(100);
  }

  async retryFailed(): Promise<void> {
    for (const [id, item] of Array.from(this.inMemoryStatus)) {
      if (item.status === "error") {
        this.inMemoryStatus.set(id, { ...item, status: "queued", progress: 0, error: undefined, retryCount: 0 });
        const dbItem = await this.getFromDB(id);
        if (dbItem) {
          dbItem.status = "queued";
          dbItem.progress = 0;
          dbItem.error = undefined;
          dbItem.retryCount = 0;
          await this.saveToDb(dbItem);
        }
      }
    }
    this.notifyListeners();
    this.kick(100);
  }

  clearCompleted(): void {
    for (const [id, item] of Array.from(this.inMemoryStatus)) {
      if (item.status === "done" || item.status === "error") {
        this.inMemoryStatus.delete(id);
        this.removeFromDB(id);
      }
    }
    this.notifyListeners();
  }

  // ─── Queue engine ────────────────────────────────────────────────

  /**
   * Kick the queue — schedule the run loop to start if it isn't already running.
   * Debounced to avoid redundant starts.
   */
  private kick(delayMs = 200): void {
    if (this.kickTimer) clearTimeout(this.kickTimer);
    this.kickTimer = setTimeout(() => {
      this.kickTimer = null;
      this.runLoop();
    }, delayMs);
  }

  /**
   * The main processing loop. Processes one item at a time, sequentially.
   * Only one instance of this loop runs at a time (guarded by isRunning).
   * This is the ONLY place that transitions items from "queued" → upload.
   */
  private async runLoop(): Promise<void> {
    if (this.isRunning) return;
    this.isRunning = true;
    this.notifyListeners();

    try {
      let consecutiveEmpty = 0;
      while (consecutiveEmpty < 2) {
        const nextId = this.findNextQueued();
        if (!nextId) {
          consecutiveEmpty++;
          // Wait a beat and check again (items might be transitioning from retry)
          if (consecutiveEmpty < 2) {
            await new Promise(r => setTimeout(r, 500));
          }
          continue;
        }
        consecutiveEmpty = 0;

        // Process this single item — fully await it before moving to next
        await this.processOneItem(nextId);

        // Small delay between items to let the UI breathe
        await new Promise(r => setTimeout(r, 300));
      }
    } catch (err) {
      console.error("[UploadQueue] runLoop error:", err);
    } finally {
      this.isRunning = false;
      this.notifyListeners();
      // Fire batch complete callbacks if any items were successfully uploaded
      if (this.completedInBatch.length > 0) {
        const completed = [...this.completedInBatch];
        this.completedInBatch = [];
        console.log(`[UploadQueue] Batch complete: ${completed.length} items uploaded`);
        this.batchCallbacks.forEach(cb => {
          try { cb(completed); } catch (e) { console.error("[UploadQueue] Batch callback error:", e); }
        });
      }
    }
  }

  private findNextQueued(): string | null {
    // Sort by addedAt to process in order
    const queued = Array.from(this.inMemoryStatus.entries())
      .filter(([, item]) => item.status === "queued")
      .sort((a, b) => a[1].addedAt - b[1].addedAt);
    return queued.length > 0 ? queued[0][0] : null;
  }

  /**
   * Process a single queued item: compress → upload → save record → done.
   * This method is fully awaited — no fire-and-forget.
   */
  private async processOneItem(id: string): Promise<void> {
    const statusItem = this.inMemoryStatus.get(id);
    if (!statusItem || statusItem.status !== "queued") return;

    try {
      // Step 1: Get file data from IndexedDB
      const dbItem = await this.getFromDB(id);
      if (!dbItem || !dbItem.arrayBuffer) {
        this.updateItemStatus(id, { status: "error", error: "File data lost — please re-select the file" });
        return;
      }

      // Step 2: Compress if applicable
      let uploadBlob: Blob = new Blob([dbItem.arrayBuffer], { type: dbItem.fileType });
      const isCompressibleImage = dbItem.fileType.startsWith("image/") &&
        !dbItem.fileType.includes("gif") &&
        !dbItem.fileType.includes("svg");

      if (isCompressibleImage && dbItem.fileSize > 500 * 1024 && dbItem.fileSize <= MAX_COMPRESS_SIZE) {
        this.updateItemStatus(id, { status: "compressing", progress: 2 });
        try {
          const compressed = await this.compressImage(uploadBlob, dbItem.fileSize);
          if (compressed.size < uploadBlob.size * 0.9) {
            console.log(`[UploadQueue] Compressed ${dbItem.fileName}: ${(dbItem.fileSize / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB`);
            uploadBlob = compressed;
          }
        } catch (compErr) {
          console.warn(`[UploadQueue] Compression failed for ${dbItem.fileName}, uploading original:`, compErr);
        }
      } else if (isCompressibleImage && dbItem.fileSize > MAX_COMPRESS_SIZE) {
        console.log(`[UploadQueue] Skipping compression for ${dbItem.fileName} (too large)`);
      }

      // Step 3: Upload to S3
      this.updateItemStatus(id, { status: "uploading", progress: 5 });
      console.log(`[UploadQueue] Uploading: ${dbItem.fileName} (${(uploadBlob.size / 1024).toFixed(0)}KB)`);

      const result = await this.doUpload(id, uploadBlob, dbItem.fileName);
      console.log(`[UploadQueue] Upload complete: ${dbItem.fileName} → ${result.url.substring(0, 60)}...`);

      // Step 4: Save document record
      this.updateItemStatus(id, { status: "saving", progress: 90 });
      await this.saveDocumentRecord(result.url, dbItem);
      console.log(`[UploadQueue] Record saved: ${dbItem.fileName}`);

      // Step 5: Done!
      this.updateItemStatus(id, { status: "done", progress: 100 });
      await this.removeFromDB(id);

      // Track for batch completion callback
      this.completedInBatch.push({
        folder: dbItem.folder,
        projectId: dbItem.projectId,
        leadId: dbItem.leadId,
        fileName: dbItem.fileName,
      });

      // Auto-clear done items after 8 seconds
      setTimeout(() => {
        if (this.inMemoryStatus.get(id)?.status === "done") {
          this.inMemoryStatus.delete(id);
          this.notifyListeners();
        }
      }, 8000);

    } catch (err: any) {
      console.error(`[UploadQueue] Failed: ${statusItem.fileName}:`, err.message);
      const currentItem = this.inMemoryStatus.get(id);
      const retryCount = (currentItem?.retryCount || 0) + 1;

      const isRetryable = !err.message?.includes("log in") &&
        !err.message?.includes("too large") &&
        !err.message?.includes("re-select");

      if (retryCount <= MAX_RETRIES && isRetryable) {
        const delay = Math.min(BASE_RETRY_DELAY * Math.pow(2, retryCount - 1), 30000);
        console.log(`[UploadQueue] Will retry ${statusItem.fileName} (attempt ${retryCount}/${MAX_RETRIES}) in ${delay}ms`);
        this.updateItemStatus(id, {
          status: "queued",
          progress: 0,
          retryCount,
          error: undefined,
        });

        // Update in IndexedDB too
        const dbItem = await this.getFromDB(id);
        if (dbItem) {
          dbItem.retryCount = retryCount;
          dbItem.status = "queued";
          await this.saveToDb(dbItem);
        }

        // Wait for the retry delay before continuing the loop
        await new Promise(r => setTimeout(r, delay));
      } else {
        this.updateItemStatus(id, {
          status: "error",
          error: err.message || "Upload failed",
          retryCount,
        });
      }
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────

  private updateItemStatus(id: string, updates: Partial<Omit<QueuedFile, "arrayBuffer">>): void {
    const current = this.inMemoryStatus.get(id);
    if (current) {
      this.inMemoryStatus.set(id, { ...current, ...updates });
      this.notifyListeners();
    }
  }

  private doUpload(id: string, blob: Blob, fileName: string): Promise<{ url: string; fileName: string; contentType: string; size: number }> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("files", blob, fileName);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/upload");
      xhr.withCredentials = true;
      xhr.timeout = 600000; // 10 minute timeout

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 80) + 5; // 5-85%
          this.updateItemStatus(id, { progress: percent });
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.files?.[0]) {
              resolve(data.files[0]);
            } else {
              reject(new Error("No file in server response"));
            }
          } catch {
            reject(new Error("Invalid server response"));
          }
        } else if (xhr.status === 401) {
          reject(new Error("Please log in again to upload files"));
        } else if (xhr.status === 413) {
          reject(new Error("File too large — maximum 100MB"));
        } else if (xhr.status === 0) {
          reject(new Error("Connection lost — will retry"));
        } else {
          reject(new Error(`Upload failed (HTTP ${xhr.status})`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error — will retry when connected"));
      xhr.ontimeout = () => reject(new Error("Upload timed out — will retry"));
      xhr.onabort = () => reject(new Error("Upload aborted — will retry"));

      xhr.send(formData);
    });
  }

  private async saveDocumentRecord(url: string, item: QueuedFile): Promise<void> {
    let lastErr: Error | null = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const resp = await fetch("/api/trpc/documents.create", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            json: {
              leadId: item.leadId || undefined,
              projectId: item.projectId || undefined,
              folder: item.folder,
              fileName: item.fileName,
              fileUrl: url,
              fileKey: url.split("/").slice(-1)[0] || item.fileName,
              fileType: item.fileType,
              fileSize: item.fileSize,
            },
          }),
        });

        if (resp.ok) return;

        const text = await resp.text().catch(() => "");
        lastErr = new Error(`Failed to save document record (${resp.status}): ${text}`);
      } catch (err: any) {
        lastErr = err;
      }

      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
      }
    }
    throw lastErr || new Error("Failed to save document record");
  }

  private compressImage(blob: Blob, originalSize: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Compression timed out"));
      }, 15000);

      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(url);

        let { width, height } = img;
        const maxDim = Math.min(
          MAX_CANVAS_DIM,
          originalSize > 5 * 1024 * 1024 ? 1400 : originalSize > 2 * 1024 * 1024 ? 1600 : 1920
        );
        const quality = originalSize > 5 * 1024 * 1024 ? 0.65 : originalSize > 2 * 1024 * 1024 ? 0.7 : 0.8;

        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Safety: cap total pixels to ~8M for mobile
        const totalPixels = width * height;
        if (totalPixels > 8_000_000) {
          const scale = Math.sqrt(8_000_000 / totalPixels);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        let canvas: HTMLCanvasElement | null = null;
        try {
          canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) { reject(new Error("No canvas context")); return; }
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (result) => {
              if (canvas) { canvas.width = 0; canvas.height = 0; }
              result ? resolve(result) : reject(new Error("Compression failed"));
            },
            "image/jpeg",
            quality
          );
        } catch (err) {
          if (canvas) { canvas.width = 0; canvas.height = 0; }
          reject(err);
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load image for compression"));
      };

      img.src = url;
    });
  }
}

// Singleton instance
export const uploadQueue = new UploadQueueManager();
